'use client';

import { useTranslations } from 'next-intl';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { Fragment, useMemo, useRef, useState, useTransition } from 'react';

import { GTMItemListName, sendGTMEvent } from '@/fo/api/gtm';
import { MindboxPageView, mindboxViewCategory } from '@/fo/api/mindbox';
import { FeatureProductTileBase } from '@/fo/feature/product/tile/base';
import {
  UiKitBannerGiftCard,
  UiKitBannerGiftCardProps
} from '@/fo/ui/kit/banner/gift-card';
import {
  DEFAULT_SORT_OPTION,
  FilterChangeArgs,
  FilterKeys,
  FilterOption,
  Filters,
  SelectedFilters,
  UiProductLayoutCatalog
} from '@/fo/ui/product/layout/catalog';
import { utilGetHighBonusLabel } from '@/fo/util/get-high-bonus-label';
import { MappedProduct } from '@/fo/util/map-product-data';

import { GetCategoriesResponse } from '../api/getCategories';
import { getProducts, GetProductsResponse } from '../api/getProducts';
import { getProductsAggregations } from '../api/getProductsAggregations';
import { getProductsCount } from '../api/getProductsCount';
import { PAGE_SIZE } from '../constants';
import { DefaultCategoryPath, PaginationParams } from '../types';
import {
  addDiscountFilterOption,
  formatCategoryParams,
  isBrandCategoryPage,
  updateFilters
} from '../util';
import { parseAsObject } from '../util/parseAsObject';

export interface FeatureProductCatalogBaseProps {
  isAuthorized: boolean;
  loyaltyLevel?: number;
  data: Partial<GetCategoriesResponse> &
    GetProductsResponse & {
      defaultCategoryPath?: DefaultCategoryPath;
      selectedFilters?: SelectedFilters;
      selectedSort?: string;
      pagination?: { page: number };
      query?: string;
    };
  topSeoBlock?: React.ReactNode;
  bottomSeoBlock?: React.ReactNode;
  topCustomBlock?: React.ReactNode;
  hiddenFiltersSlug?: string[];
  hideCareOptions?: boolean;
  giftCardBanner?: UiKitBannerGiftCardProps | null;
  listName: GTMItemListName;
}

export function FeatureProductCatalogBase({
  isAuthorized,
  loyaltyLevel,
  data: initialData,
  topSeoBlock,
  bottomSeoBlock,
  topCustomBlock,
  hiddenFiltersSlug,
  hideCareOptions = false,
  giftCardBanner,
  listName
}: FeatureProductCatalogBaseProps) {
  const t = useTranslations();

  // Проверяем, является ли текущая страница страницей бренда по URL
  const isBrandPage = isBrandCategoryPage(initialData?.category);

  // Расширяем hiddenFiltersSlug, добавляя 'brand' на страницах брендов
  const effectiveHiddenFiltersSlug = isBrandPage
    ? [...(hiddenFiltersSlug || []), 'brand']
    : hiddenFiltersSlug;

  const selectedFilters = useRef<SelectedFilters>({});
  const [tempFilters, setTempFilters] = useState<SelectedFilters>(
    initialData?.selectedFilters || {}
  );
  const [selectedSort, setSelectedSort] = useState<string>(
    initialData?.selectedSort || DEFAULT_SORT_OPTION.value
  );

  const [products, setProducts] = useState<MappedProduct[]>(
    initialData.products || []
  );

  // Фильтруем начальные фильтры, удаляя brand на страницах брендов
  const [filters, setFilters] = useState<Filters>(() => {
    const filteredInitialFilters = isBrandPage
      ? {
          ...initialData.filters,
          mainFilters: initialData.filters.mainFilters.filter(
            (filter) => filter.slug !== 'brand'
          )
        }
      : initialData.filters;

    return addDiscountFilterOption(filteredInitialFilters, t);
  });
  const [isLoading, startTransition] = useTransition();
  const [pagination, setPagination] = useState<PaginationParams>({
    count: initialData.page_info?.total_pages || 0,
    page:
      initialData?.pagination?.page || initialData.page_info?.current_page || 1
  });
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const [productsCount, setProductsCount] = useState<number>(
    initialData?.total_count || 0
  );

  const gtmEventCategory = useMemo(() => {
    const category = initialData?.category as {
      name?: string;
      breadcrumbs?: { category_name?: string | null }[] | null;
    } | null;

    if (!category) return '';

    const mapName = (name: string) =>
      name === 'Default Category' ? t('catalog') : name;

    const breadcrumbLabels = [
      ...(category.breadcrumbs || [])
        .filter(Boolean)
        .map((breadcrumb) => mapName(String(breadcrumb?.category_name || ''))),
      mapName(category.name || '')
    ].filter(Boolean);

    return breadcrumbLabels.join('::') || t('catalog');
  }, [initialData?.category, t]);

  const [, setSearchQuery] = useQueryStates(
    {
      filters: parseAsObject.withDefault(initialData?.selectedFilters || {}),
      sort: parseAsString.withDefault(
        initialData?.selectedSort || DEFAULT_SORT_OPTION.value
      ),
      page: parseAsInteger.withDefault(1)
    },
    {
      shallow: false,
      urlKeys: {
        filters: 'f',
        sort: 's',
        page: 'p'
      }
    }
  );

  const updateQueryState = (
    partial: Partial<{
      filters: SelectedFilters | null;
      sort: string;
      page: number | null;
    }>
  ) => {
    if (partial.filters && Object.keys(partial.filters).length === 0) {
      partial.filters = null;
    }
    if (typeof partial.page === 'number' && partial.page <= 1) {
      partial.page = null;
    }
    setSearchQuery(partial, { history: 'push' });
  };

  const pickUpCareOptions = useMemo(() => {
    return filters.pickUpCareFilters.map((option) => option.slug);
  }, [filters]);

  const mainFilterSlugs = useMemo(() => {
    return filters.mainFilters.map((option) => option.slug);
  }, [filters]);

  const getProductsParams = ({
    filtersParams,
    paginationParams,
    sortValueParams
  }: {
    filtersParams?: SelectedFilters;
    paginationParams?: PaginationParams;
    sortValueParams?: string;
  }) => {
    const newSelectedFilters = filtersParams || tempFilters;
    return formatCategoryParams({
      filtersParams: newSelectedFilters,
      paginationParams: paginationParams || pagination,
      sortValue: sortValueParams || selectedSort,
      defaultCategoryPath: initialData.defaultCategoryPath,
      category: initialData?.category,
      search: initialData?.query
    });
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setIsOpenModal(true);
      return;
    }
    setTempFilters(selectedFilters.current);
  };

  const onReset = (filterKey: FilterKeys) => {
    const newTempFilters = resetFiltersByKey(tempFilters, filterKey);
    setTempFilters(newTempFilters);
    updateQueryState({ filters: newTempFilters });
    onShowProducts(
      getProductsParams({ filtersParams: newTempFilters }),
      newTempFilters
    );
  };

  const resetFiltersByKey = (
    currentFilters: SelectedFilters,
    filterKey: FilterKeys
  ): SelectedFilters => {
    return Object.entries(currentFilters).reduce<SelectedFilters>(
      (acc, [key, value]) => {
        if (
          filterKey === FilterKeys.mainFilters &&
          mainFilterSlugs.includes(key)
        ) {
          return acc;
        }
        if (
          filterKey === FilterKeys.pickUpCareFilters &&
          pickUpCareOptions.includes(key)
        ) {
          return acc;
        }
        acc[key] = value;
        return acc;
      },
      {}
    );
  };

  const fetchProductsCount = async (
    props: ReturnType<typeof getProductsParams>
  ) => {
    const count = await getProductsCount(props);
    setProductsCount(count);
  };

  const fetchProductsAggregations = async (
    props: ReturnType<typeof getProductsParams>
  ) => {
    const { total_count, filters: newFilters } =
      await getProductsAggregations(props);

    setProductsCount(total_count);
    if (newFilters.pickUpCareFilters.length > 0) {
      setFilters((prev) => ({
        ...prev,
        pickUpCareFilters: newFilters.pickUpCareFilters
      }));
    }
  };

  const onFiltersChange = (args: FilterChangeArgs, withUpdate = false) => {
    const updatedFilters = updateFilters(tempFilters, args);
    setTempFilters(updatedFilters);

    if (withUpdate) {
      onShowProducts(
        getProductsParams({ filtersParams: updatedFilters }),
        updatedFilters
      );
      updateQueryState({ filters: updatedFilters });
    } else {
      const props = getProductsParams({
        filtersParams: updatedFilters
      });

      if (args.key === FilterKeys.pickUpCareFilters) {
        if (args.checked) {
          const filter = filters.pickUpCareFilters.find(
            (f) => f.slug === args.slug
          );
          const option = filter?.options.find((o) => o.value === args.value);

          if (option && option.count === 0) {
            setProductsCount(0);
            return;
          }
        }

        fetchProductsAggregations(props);
      } else {
        fetchProductsCount(props);
      }
    }
  };

  const onShowProducts = async (
    props: ReturnType<typeof getProductsParams>,
    newSelectedFilters?: SelectedFilters
  ) => {
    startTransition(async () => {
      const response = await getProducts(props, effectiveHiddenFiltersSlug);
      setFilters(addDiscountFilterOption(response.filters, t));
      setProducts(response.products);
      setProductsCount(response.total_count);
      setPagination({
        count: response.page_info?.total_pages || 0,
        page: response.page_info?.current_page || 1
      });
      setIsOpenModal(false);
      selectedFilters.current = newSelectedFilters || tempFilters;
    });
  };

  const handleSortChange = (option: FilterOption | null) => {
    const newSortValue = option?.value || '';
    const paginationParams = {
      count: pagination.count,
      page: 1
    };
    if (option?.label) {
      sendGTMEvent({
        event: 'sort_click',
        eventCategory: gtmEventCategory,
        eventLabel: option.label
      } as Parameters<typeof sendGTMEvent>[0]);
    }
    setSelectedSort(newSortValue);
    updateQueryState({ sort: newSortValue, page: paginationParams?.page });
    onShowProducts(
      getProductsParams({
        sortValueParams: newSortValue,
        paginationParams
      })
    );
  };

  const updatePagination = (
    paginationParams: Omit<PaginationParams, 'pageSize'>
  ) => {
    setPagination(paginationParams);
    onShowProducts(getProductsParams({ paginationParams }));
    updateQueryState({ page: paginationParams?.page || pagination.page });
  };

  const onLoadMore = () => {
    const nextPage = (pagination.page || 1) + 1;
    const paginationParams = {
      count: pagination.count,
      page: nextPage,
      pageSize: PAGE_SIZE
    };
    startTransition(async () => {
      const response = await getProducts(
        getProductsParams({ paginationParams }),
        effectiveHiddenFiltersSlug
      );
      updateQueryState({ page: paginationParams?.page || pagination.page });
      setProducts((prev) => [...prev, ...response.products]);
      setFilters(addDiscountFilterOption(response.filters, t));
      setProductsCount(response.total_count);
      setPagination({
        count: response.page_info?.total_pages || 0,
        page: nextPage
      });
      selectedFilters.current = tempFilters;
    });
  };

  return (
    <>
      <MindboxPageView
        action={mindboxViewCategory}
        params={String(
          initialData?.defaultCategoryPath?.id ||
            initialData?.category?.id ||
            ''
        )}
      />
      <UiProductLayoutCatalog
        pagination={{
          count: pagination?.count || 0,
          page: pagination?.page || 0
        }}
        filters={filters}
        topFilters={initialData.topFilters}
        selectedFilters={tempFilters}
        selectedSort={selectedSort}
        onFiltersChange={onFiltersChange}
        onShowProducts={() => {
          onShowProducts(getProductsParams({}));
          updateQueryState({ filters: tempFilters });
        }}
        productsCount={productsCount}
        onOpenChange={onOpenChange}
        onReset={onReset}
        onSortChange={handleSortChange}
        setPagination={updatePagination}
        isLoading={isLoading}
        topSeoBlock={topSeoBlock}
        topCustomBlock={topCustomBlock}
        bottomSeoBlock={bottomSeoBlock}
        onLoadMore={onLoadMore}
        isOpenModal={isOpenModal}
        isCatalog
        hideCareOptions={hideCareOptions}
        hideSort={!!initialData.query}
        hideFilters={!!initialData.query && !initialData.products?.length}
        isSearchPage={!!initialData.query}
        gtmEventCategory={gtmEventCategory}
      >
        {products.map((productData: MappedProduct, index: number) => {
          const key = `${productData.sku}-${index}`;
          return (
            <Fragment key={key}>
              <FeatureProductTileBase
                isAuthorized={isAuthorized}
                enableMicrodata
                data={{
                  ...productData,
                  labelsBonus: [
                    ...productData.labelsBonus,
                    ...(utilGetHighBonusLabel(
                      productData.earnedBonusPoints,
                      productData.finalPrice ?? productData.basePrice,
                      loyaltyLevel,
                      isAuthorized
                    )
                      ? [{ name: t('Increased cashback'), color: '' }]
                      : [])
                  ]
                }}
                isCatalog
                listName={listName}
                positionIndex={index}
              />
              {giftCardBanner && index === 1 && (
                <div className="col-span-full sm:hidden">
                  <UiKitBannerGiftCard
                    {...(giftCardBanner as UiKitBannerGiftCardProps)}
                  />
                </div>
              )}
              {giftCardBanner && index === 2 && (
                <div className="col-span-full hidden sm:block md:hidden">
                  <UiKitBannerGiftCard
                    {...(giftCardBanner as UiKitBannerGiftCardProps)}
                  />
                </div>
              )}
              {giftCardBanner && index === 3 && (
                <div className="col-span-full hidden md:block">
                  <UiKitBannerGiftCard
                    {...(giftCardBanner as UiKitBannerGiftCardProps)}
                  />
                </div>
              )}
            </Fragment>
          );
        })}
      </UiProductLayoutCatalog>
    </>
  );
}
